import React from "react";

import { MessageDescriptor } from "@lingui/core";
import { isEmpty } from "lodash/fp";

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  SelectProps,
  styled,
  SxProps,
} from "@mui/material";

import { DefaultBadge } from "@readai/common/components/Badge";
import { ELLIPSIS_SX } from "@readai/common/components/styles";
import TooltipWrapper from "@readai/common/components/TooltipWrapper";
import useTranslatedText from "@readai/common/hooks/useTranslatedText";
import ChevronDown from "@readai/icons/uui/ChevronDown";
import { Row } from "@readai/pulse/components/Stack";

import { DROPDOWN_STYLES, TEXT_VARIANT_STYLES } from "./styles";
import { DropdownItem } from "./types";

export interface DropdownProps<T extends string = string>
  extends Omit<SelectProps<T>, "variant"> {
  placeholder?: string;
  name: string;
  selectedItem?: DropdownItem<T>;
  sx?: SxProps;
  sxLabel?: SxProps;
  tooltip?: string | MessageDescriptor;
  variant?: "text" | "outlined";
  wrapperSx?: SxProps;
  renderDefaultLabel?: () => React.ReactNode;
  renderSelectedItem?: (selectedItem: DropdownItem<T>) => React.ReactNode;
  ref?: React.Ref<HTMLElement>;
}

const StyledFormLabel = styled(FormLabel)({ textTransform: "uppercase" });

const DefaultRenderValue = <T extends string = string>({
  defaultValue,
  selectedItem,
  placeholder,
  renderDefaultLabel,
  renderSelectedItem,
  disabled,
}: {
  selectedItem?: DropdownItem<T>;
  defaultValue?: string;
  placeholder?: string;
  renderDefaultLabel?: () => React.ReactNode;
  renderSelectedItem?: (selectedItem: DropdownItem<T>) => React.ReactNode;
  disabled?: boolean;
}) => {
  if (!selectedItem) return <>{placeholder ?? ""}</>;

  const isDefault = selectedItem.value === defaultValue;

  return (
    <Row
      variant="left"
      spacing={0.5}
      sx={{ color: "text.med", ...ELLIPSIS_SX }}
    >
      {renderSelectedItem ? (
        renderSelectedItem(selectedItem)
      ) : (
        <>
          {selectedItem.label ?? placeholder ?? ""}
          {isDefault &&
            (renderDefaultLabel ? (
              renderDefaultLabel()
            ) : (
              <DefaultBadge disabled={disabled} sx={{ ml: 1 }} />
            ))}
        </>
      )}
    </Row>
  );
};

const Dropdown = <T extends string = string>({
  disabled,
  error,
  placeholder,
  label,
  name,
  sx,
  sxLabel,
  inputProps,
  tooltip,
  defaultValue,
  variant,
  selectedItem,
  wrapperSx,
  renderDefaultLabel,
  renderSelectedItem,
  ref,
  ...selectProps
}: DropdownProps<T>) => {
  const { getTranslatedMessage } = useTranslatedText();

  const id = `${name}-select`;

  return (
    <FormControl error={error} sx={wrapperSx} fullWidth={selectProps.fullWidth}>
      {label && (
        <StyledFormLabel htmlFor={id} sx={sxLabel}>
          {label}
        </StyledFormLabel>
      )}
      <TooltipWrapper
        arrow
        title={getTranslatedMessage(tooltip)}
        wrapperSx={{
          width: "100%",
          "& .MuiInputBase-root": { width: "100%" },
        }}
      >
        <Select
          id={id}
          value={selectedItem?.value ?? ""}
          sx={{
            // TODO: make this a styled component instead of passing these styles to the sx prop
            ...DROPDOWN_STYLES,
            ...(variant === "text" && TEXT_VARIANT_STYLES),
            ...sx,
          }}
          ref={ref}
          displayEmpty
          disabled={disabled || isEmpty(selectProps.children)}
          IconComponent={ChevronDown}
          renderValue={() => (
            <DefaultRenderValue
              placeholder={placeholder}
              defaultValue={defaultValue}
              selectedItem={selectedItem}
              renderDefaultLabel={renderDefaultLabel}
              renderSelectedItem={renderSelectedItem}
              disabled={disabled}
            />
          )}
          MenuProps={{
            disableScrollLock: true,
            sx: {
              "& .MuiMenu-paper": {
                backgroundColor: "elevation.surface.raised",
                ...selectProps?.MenuProps?.PaperProps,
              },
            },
          }}
          {...selectProps}
        >
          {selectProps.children}
        </Select>
      </TooltipWrapper>
      {inputProps?.helperText && (
        <FormHelperText>{inputProps.helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default Dropdown;
